import { useEffect, useState } from "react";
import { Menu, CalendarCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { Logo } from "@/components/Logo";
import { NAV_LINKS } from "@/lib/siteData";
import { cn } from "@/lib/utils";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-all duration-300",
        scrolled
          ? "border-b border-border/70 bg-background/85 backdrop-blur-xl shadow-sm"
          : "bg-transparent"
      )}
    >
      <nav className="mx-auto flex h-[68px] max-w-7xl items-center justify-between px-5 lg:px-8">
        <a href="#home" aria-label="Anchor Web Systems home">
          <Logo variant={scrolled ? "light" : "dark"} />
        </a>

        <ul className="hidden items-center gap-1 lg:flex">
          {NAV_LINKS.map((link) => (
            <li key={link.href}>
              <a
                href={link.href}
                className={cn(
                  "rounded-full px-3.5 py-2 text-sm font-medium transition-colors",
                  scrolled
                    ? "text-muted-foreground hover:bg-secondary hover:text-navy"
                    : "text-navy-foreground/80 hover:bg-white/10 hover:text-navy-foreground"
                )}
              >
                {link.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-2">
          <Button asChild variant="cta" className="hidden sm:inline-flex">
            <a href="#demo">
              <CalendarCheck className="h-4 w-4" />
              Schedule a Demo
            </a>
          </Button>

          <Sheet>
            <SheetTrigger asChild>
              <Button
                variant={scrolled ? "outline" : "hero"}
                size="icon"
                className="lg:hidden"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] border-l border-border bg-background">
              <div className="mb-8 mt-2">
                <Logo variant="light" />
              </div>
              <ul className="flex flex-col gap-1">
                {NAV_LINKS.map((link) => (
                  <li key={link.href}>
                    <SheetClose asChild>
                      <a
                        href={link.href}
                        className="block rounded-xl px-4 py-3 text-base font-medium text-navy transition-colors hover:bg-secondary"
                      >
                        {link.label}
                      </a>
                    </SheetClose>
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-col gap-3">
                <SheetClose asChild>
                  <Button asChild variant="cta" size="lg">
                    <a href="#demo">
                      <CalendarCheck className="h-4 w-4" /> Schedule a Demo
                    </a>
                  </Button>
                </SheetClose>
                <SheetClose asChild>
                  <Button asChild variant="silver" size="lg">
                    <a href="#preview">See the Platform in Action</a>
                  </Button>
                </SheetClose>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};
